// api.js - файл для работы с API AmoCRM

// Базовый URL API (измените на ваш адрес сервера)
const API_BASE_URL = "https://api.ai-osnova.ru/api";

// Максимальное количество повторных попыток при сбое сети
const MAX_RETRY_ATTEMPTS = 3;
// Задержка между повторными попытками (в мс)
const RETRY_DELAY = 2000;
// Стандартный таймаут для длительных операций (10 минут)
const DEFAULT_TIMEOUT = 600000;

// Новые функции API

/**
 * Запускает процесс экспорта звонков из AmoCRM.
 * @param {object} params - Параметры для экспорта.
 * @param {string} params.client_id - ID клиента.
 * @param {string} params.start_date - Дата начала в формате DD.MM.YYYY.
 * @param {string} params.end_date - Дата окончания в формате DD.MM.YYYY.
 * @returns {Promise<object>} - Ответ от API.
 */
export const startCallsExport = async (params) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/calls-events/export`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(params),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || data.message || "Ошибка при запуске экспорта звонков");
    }
    return data;
  } catch (error) {
    console.error("Ошибка при запуске экспорта звонков:", error);
    throw error;
  }
};

/**
 * Получает результаты экспорта звонков.
 * @param {string} clientId - ID клиента.
 * @param {string} startDate - Дата начала в формате DD.MM.YYYY.
 * @param {string} endDate - Дата окончания в формате DD.MM.YYYY.
 * @returns {Promise<object>} - Ответ от API.
 */
export const getCallsExportResults = async (clientId, startDate, endDate) => {
  const url = new URL(`${API_BASE_URL}/calls-events/export-results`);
  url.searchParams.append("client_id", clientId);
  url.searchParams.append("start_date", startDate);
  url.searchParams.append("end_date", endDate);

  console.log('[API] getCallsExportResults - URL:', url.toString());

  try {
    const response = await apiFetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    console.log('[API] getCallsExportResults - Response status:', response.status);

    // Ответ 404 Not Found означает, что процесс еще выполняется
    if (response.status === 404) {
      console.log('[API] getCallsExportResults - Процесс еще выполняется (404)');
      return { processing: true };
    }

    const data = await response.json();
    console.log('[API] getCallsExportResults - Response data:', data);

    if (!response.ok) {
      throw new Error(data.detail || data.message || "Ошибка при получении результатов экспорта");
    }
    return data;
  } catch (error) {
    console.error("[API] getCallsExportResults - Error:", error);
    // Не пробрасываем ошибку дальше, если это ошибка сети во время поллинга
    if (error.message.includes("Failed to fetch")) {
      return { processing: true, error: error.message };
    }
    throw error;
  }
};

/**
 * Получает баланс токенов ElevenLabs.
 * @returns {Promise<object>} - Информация о балансе.
 */
export const getElevenLabsBalance = async () => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/elevenlabs-balance`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || data.message || "Ошибка при получении баланса ElevenLabs");
    }
    // Приводим новый формат ответа к старому, который ожидает фронтенд
    return {
      total_tokens: data.character_limit,
      remaining_tokens: data.tokens_remaining,
      minutes_remaining: data.minutes_remaining,
    };
  } catch (error) {
    console.error("Ошибка при получении баланса ElevenLabs:", error);
    throw error;
  }
};

// API-ключ для авторизации запросов
const API_KEY = (process.env.REACT_APP_API_KEY || "").trim();

// Обёртка над fetch, автоматически добавляющая X-API-Key
const apiFetch = (url, options = {}) => {
  const headers = { "X-API-Key": API_KEY, ...options.headers };
  return fetch(url, { ...options, headers });
};

// Функция задержки
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Функция для повторных попыток выполнения запроса при сбоях сети
const fetchWithRetry = async (
  url,
  options,
  maxRetries = MAX_RETRY_ATTEMPTS,
  delay = RETRY_DELAY
) => {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(
          `Повторная попытка запроса (${attempt}/${maxRetries - 1})...`
        );
      }

      return await apiFetch(url, options);
    } catch (error) {
      console.warn(
        `Запрос не удался (попытка ${attempt + 1}/${maxRetries}):`,
        error.message
      );
      lastError = error;

      // Повторяем только при сетевых ошибках, не при ошибках сервера
      if (
        !error.message.includes("Failed to fetch") &&
        !error.message.includes("NetworkError") &&
        !error.message.includes("network error")
      ) {
        throw error;
      }

      if (attempt < maxRetries - 1) {
        console.log(
          `Ожидание перед следующей попыткой: ${delay / 1000} сек...`
        );
        await sleep(delay);
      }
    }
  }

  throw lastError;
};

// Добавляем функцию для обработки ошибок API, включая автоматическое обновление токенов
const handleApiError = async (
  error,
  clientId,
  retryFunction,
  ...retryParams
) => {
  console.error("API Error:", error);

  // Проверяем, содержит ли сообщение об ошибке информацию о недействительном токене
  const isAuthError =
    (error.message &&
      (error.message.includes("Unauthorized") ||
        error.message.includes("unauthorized") ||
        error.message.includes("авторизац") ||
        error.message.includes("токен") ||
        error.message.includes("401"))) ||
    error.status === 401;

  // Если это ошибка авторизации и передан clientId, пробуем обновить токен
  if (isAuthError && clientId) {
    console.log(
      `Обнаружена ошибка авторизации для клиники ${clientId}, пробуем обновить токен...`
    );

    try {
      // Обновляем токен для данной клиники
      const tokenResponse = await refreshClinicToken(clientId);

      if (tokenResponse.success) {
        console.log(
          `Токен для клиники ${clientId} успешно обновлен, повторяем запрос...`
        );

        // Если функция для повторного вызова передана, вызываем её с теми же параметрами
        if (typeof retryFunction === "function") {
          return await retryFunction(...retryParams);
        }
      } else {
        console.error(
          `Не удалось обновить токен для клиники ${clientId}:`,
          tokenResponse.message
        );
      }
    } catch (refreshError) {
      console.error(
        `Ошибка при обновлении токена для клиники ${clientId}:`,
        refreshError
      );
    }
  }

  // Если это не ошибка авторизации или не удалось обновить токен, пробрасываем ошибку дальше
  throw error;
};



/**
 * Получение сделок по дате
 * @param {string} clientId - ID клиента AmoCRM
 * @param {string} date - Дата в формате ДД.ММ.ГГГГ
 * @returns {Promise} - Ответ от API
 */
export const getLeadsByDate = async (clientId, date) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/amocrm/leads/by-date`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        client_id: clientId,
        date: date,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при обращении к API");
    }

    return data;
  } catch (error) {
    // Используем обработчик ошибок с возможностью повторного запроса
    return handleApiError(error, clientId, getLeadsByDate, clientId, date);
  }
};

/**
 * Получение сделок по диапазону дат
 * @param {string} clientId - ID клиента AmoCRM
 * @param {string} dateFrom - Начальная дата в формате ДД.ММ.ГГГГ
 * @param {string} dateTo - Конечная дата в формате ДД.ММ.ГГГГ
 * @param {boolean} includeContacts - Включать ли контакты
 * @returns {Promise} - Ответ от API
 */
export const getLeadsByDateRange = async (
  clientId,
  dateFrom,
  dateTo,
  includeContacts = false
) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/amocrm/leads/by-date-range`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        client_id: clientId,
        date_from: dateFrom,
        date_to: dateTo,
        include_contacts: includeContacts,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при обращении к API");
    }

    return data;
  } catch (error) {
    // Используем обработчик ошибок с возможностью повторного запроса
    return handleApiError(
      error,
      clientId,
      getLeadsByDateRange,
      clientId,
      dateFrom,
      dateTo,
      includeContacts
    );
  }
};

/**
 * Получение контакта из сделки
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} leadId - ID сделки
 * @returns {Promise} - Ответ от API
 */
/**
 * Получение звонков по ID сделки из нашей базы данных
 * @param {string} clientId - ID клиента
 * @param {number} leadId - ID сделки
 * @returns {Promise<object>} - Ответ от API со списком звонков
 */
export const getCallsByLeadId = async (clientId, leadId) => {
  try {
    const url = new URL(`${API_BASE_URL}/calls/list`);
    url.searchParams.append("client_id", clientId);
    url.searchParams.append("lead_id", leadId);



    console.log(`Запрос звонков для сделки ${leadId}: ${url.toString()}`);

    const response = await apiFetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении звонков по сделке");
    }

    return {
      success: data.success,
      calls: data.data?.calls || [],
      message: data.message,
    };
  } catch (error) {
    console.error(`Ошибка при загрузке звонков для сделки ${leadId}:`, error);
    return {
      success: false,
      calls: [],
      message: error.message || "Неизвестная ошибка сети",
    };
  }
};

/**
 * Получение контакта из сделки
 * @deprecated Используйте getCallsByLeadId для получения данных напрямую из нашей БД
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} leadId - ID сделки
 * @returns {Promise} - Ответ от API
 */
export const getLeadContact = async (clientId, leadId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/amocrm/lead/contact`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        client_id: clientId,
        lead_id: leadId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Произошла ошибка при получении контакта"
      );
    }

    return data;
  } catch (error) {
    console.warn("Вызов устаревшей функции getLeadContact. Используйте getCallsByLeadId.");
    console.error("Error fetching lead contact:", error);
    throw error;
  }
};

/**
 * Получение звонков контакта - УСТАРЕЛ, использовать getLeadsFromCalls
 * @deprecated Используйте getLeadsFromCalls
 */
export const getContactCalls = async (clientId, contactId) => {
  console.warn("Вызов устаревшей функции getContactCalls. Используйте getCallsByLeadId.");
  console.warn("Метод getContactCalls устарел. Используйте getLeadsFromCalls");
  try {
    const response = await apiFetch(`${API_BASE_URL}/amocrm/contact/calls`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        client_id: clientId,
        contact_id: contactId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении звонков");
    }

    return data;
  } catch (error) {
    console.error("Error fetching contact calls:", error);
    throw error;
  }
};

/**
 * Скачивание и транскрибация звонка
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} noteId - ID записи звонка
 * @param {number} contactId - ID контакта
 * @param {number} leadId - ID сделки
 * @param {boolean} isFirstContact - Первый контакт или нет
 * @returns {Promise} - Ответ от API
 */
export const downloadAndTranscribeCall = async (
  clientId,
  noteId,
  contactId,
  leadId,
  isFirstContact = false
) => {
  try {
    console.log(
      "Используется старая функция транскрибации, попытка использовать новый API..."
    );

    // Получаем данные о сделке, чтобы найти ID звонка
    const callsData = await getLeadsFromCalls(null, null, clientId, 100, 0);

    // Ищем звонок с соответствующим note_id
    let callId = null;
    if (callsData && callsData.calls) {
      const call = callsData.calls.find(
        (call) =>
          call.note_id === Number(noteId) && call.lead_id === Number(leadId)
      );

      if (call) {
        callId = call._id;
        console.log(`Найден звонок с ID: ${callId} для note_id: ${noteId}`);

        // Используем новый метод API
        return await downloadAndTranscribeCallById(callId, 2);
      }
    }

    // Если не удалось найти звонок, используем старый метод
    console.log("Не удалось найти звонок в базе, используем старый API метод");
    const response = await apiFetch(
      `${API_BASE_URL}/amocrm/contact/call/${noteId}/download-and-transcribe?client_id=${clientId}&num_speakers=2&lead_id=${leadId}&contact_id=${contactId}&is_first_contact=${isFirstContact}`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Произошла ошибка при транскрибации звонка"
      );
    }

    return data;
  } catch (error) {
    console.error("Error transcribing call:", error);
    throw error;
  }
};

/**
 * Скачивание звонка
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} noteId - ID записи звонка
 * @param {number} contactId - ID контакта
 * @returns {string} - URL для скачивания
 * @deprecated Используйте downloadCallAudio с MongoDB ID
 */
export const downloadCall = (clientId, noteId, contactId) => {
  console.warn(
    "Метод downloadCall устарел. Используйте downloadCallAudio с MongoDB ID"
  );
  // Сначала проверим, есть ли MongoDB ID в локальном состоянии
  // Если нет, используем старый метод
  console.log("Запрос на скачивание звонка для note_id:", noteId);

  // Ищем звонок с таким note_id в базе данных
  return `${API_BASE_URL}/amocrm/contact/call/${noteId}/download?client_id=${clientId}&contact_id=${contactId}`;
};

/**
 * Скачивание файла транскрипции
 * @param {string} filename - Имя файла транскрипции
 * @returns {string} - URL для скачивания
 */
export const downloadTranscription = (filename) => {
  const safe = encodeURIComponent(filename || "");
  return `${API_BASE_URL}/transcriptions/${safe}/download`;
};

/**
 * Скачивание файла анализа
 * @param {string} filename - Имя файла анализа
 * @returns {string} - URL для скачивания
 */
export const downloadAnalysis = (filename) => {
  const safe = encodeURIComponent(filename || "");
  return `${API_BASE_URL}/analysis/${safe}/download`;
};

/**
 * Тестовая транскрибация и анализ загруженного MP3
 * @param {string} apiKey - API ключ для заголовка X-API-Key
 * @param {File} file - MP3 файл
 * @returns {Promise<object>} - Ответ сервера
 */
export const testTranscribeAndAnalyze = async (apiKey, file) => {
  if (!file) throw new Error("Не выбран файл для загрузки");
  const formData = new FormData();
  // Совместимость с бэкендом: возможные имена поля — 'file' или 'audio'
  formData.append("file", file);
  formData.append("audio", file);

  const response = await apiFetch(`${API_BASE_URL}/test/transcribe-and-analyze`, {
    method: "POST",
    headers: {
      "X-API-Key": apiKey || "",
    },
    body: formData,
  });

  const data = await response.json().catch(() => ({ success: false, message: "Invalid JSON" }));
  if (!response.ok || data.success === false) {
    throw new Error(data.message || `Ошибка запроса (${response.status})`);
  }
  return data;
};

/**
 * Получить URL для скачивания записи звонка
 * @param {string} callId - ID звонка (MongoDB _id)
 * @returns {string} - URL для скачивания
 */
export const getCallRecordingDownloadUrl = (callId) => {
  return `${API_BASE_URL}/calls/download/${callId}`;
};

/**
 * Обновление токена клиники
 * @param {string} clinicId - ID клиники
 * @param {string} clientSecret - Client Secret (опционально)
 * @param {string} redirectUrl - URL перенаправления (опционально)
 * @returns {Promise} - Ответ от API
 */
export const refreshClinicToken = async (
  clinicId,
  clientSecret = null,
  redirectUrl = null
) => {
  try {
    console.log(`Запрос на обновление токена для клиники ${clinicId}`);

    // Создаем объект для тела запроса
    const requestBody = {};

    // Добавляем параметры только если они предоставлены
    if (clientSecret) {
      requestBody.client_secret = clientSecret;
    }

    if (redirectUrl) {
      requestBody.redirect_url = redirectUrl;
    }

    const response = await apiFetch(
      `${API_BASE_URL}/admin/clinics/${clinicId}/refresh-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      console.error(
        `Ошибка обновления токена для клиники ${clinicId}:`,
        data.message
      );
      throw new Error(data.message || "Произошла ошибка при обновлении токена");
    }

    console.log(`Токен для клиники ${clinicId} успешно обновлен`);
    return data;
  } catch (error) {
    console.error(
      `Ошибка при обновлении токена для клиники ${clinicId}:`,
      error
    );
    throw error;
  }
};
/**
 * Получение списка всех клиник
 * @returns {Promise<Array>} - Список клиник
 */
/**
 * Получение статистики звонков для клиники
 * @param {string} clientId - ID клиента (клиники)
 * @returns {Promise<object>} - Статистика звонков (количество, мин/макс даты)
 */
export const getCallStatsForClinic = async (clientId) => {
  try {
    const url = new URL(`${API_BASE_URL}/calls/list`);
    url.searchParams.append('client_id', clientId);
    // Запрашиваем все звонки, можно поставить большой лимит, если API не отдает все по умолчанию
    url.searchParams.append('limit', '100000');


    const response = await apiFetch(url.toString());
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.detail || result.message || "Ошибка при получении звонков клиники");
    }

    const calls = result.data?.calls || [];
    if (calls.length === 0) {
      return { count: 0, from: null, to: null };
    }

    const dates = calls.map(call => new Date(call.created_date_for_filtering));
    const minDate = new Date(Math.min.apply(null, dates));
    const maxDate = new Date(Math.max.apply(null, dates));

    const formatDate = (date) => {
      const d = new Date(date);
      let day = ('0' + d.getDate()).slice(-2);
      let month = ('0' + (d.getMonth() + 1)).slice(-2);
      let year = d.getFullYear();
      return `${day}.${month}.${year}`;
    }

    return {
      count: calls.length,
      from: formatDate(minDate),
      to: formatDate(maxDate),
    };

  } catch (error) {
    console.error('Error fetching call stats for clinic:', error);
    throw error;
  }
};

export const getClinics = async () => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/clinics`);
    const result = await response.json();
    if (!response.ok) {
      const errorMessage = result.message || result.detail || "Ошибка при получении списка клиник";
      throw new Error(errorMessage);
    }
    // Бэкенд возвращает массив клиник в корне ответа
    return result;
  } catch (error) {
    console.error("Error fetching clinics:", error);
    throw error;
  }
};

/**
 * Синхронизация звонков и транскрипция для клиники
 * @param {string} clientId - ID клиента (клиники)
 * @param {string} startDate - Начальная дата в формате DD.MM.YYYY
 * @param {string} endDate - Конечная дата в формате DD.MM.YYYY
 * @returns {Promise<object>} - Результат синхронизации и транскрипции
 */
export const syncAndTranscribeClinicData = async (clientId, startDate, endDate) => {
  try {
    // Шаг 1: Синхронизация звонков
    const syncUrl = new URL(`${API_BASE_URL}/calls-parallel-bulk/sync-by-date-range`);
    syncUrl.searchParams.append('start_date_str', startDate);
    syncUrl.searchParams.append('end_date_str', endDate);
    syncUrl.searchParams.append('client_id', clientId);

    const syncResponse = await apiFetch(syncUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!syncResponse.ok) {
      const syncResult = await syncResponse.json();
      throw new Error(syncResult.detail || syncResult.message || "Ошибка при синхронизации звонков");
    }

    // Шаг 2: Транскрипция звонков
    const transcribeUrl = new URL(`${API_BASE_URL}/calls/transcribe-by-date-range`);
    transcribeUrl.searchParams.append('start_date_str', startDate);
    transcribeUrl.searchParams.append('end_date_str', endDate);
    transcribeUrl.searchParams.append('client_id', clientId);

    const transcribeResponse = await apiFetch(transcribeUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const transcribeResult = await transcribeResponse.json();
    if (!transcribeResponse.ok) {
      throw new Error(transcribeResult.detail || transcribeResult.message || "Ошибка при транскрипции звонков");
    }

    return transcribeResult;
  } catch (error) {
    console.error('Error syncing and transcribing clinic data:', error);
    throw error;
  }
};

/**
 * Анализ звонков для клиники
 * @param {string} clientId - ID клиента (клиники)
 * @param {string} startDate - Начальная дата в формате DD.MM.YYYY
 * @param {string} endDate - Конечная дата в формате DD.MM.YYYY
 * @returns {Promise<object>} - Результат анализа
 */
export const analyzeClinicData = async (clientId, startDate, endDate) => {
  try {
    // Шаг 1: Анализ звонков
    const analyzeUrl = new URL(`${API_BASE_URL}/analyze-by-date-range`);
    analyzeUrl.searchParams.append('start_date_str', startDate);
    analyzeUrl.searchParams.append('end_date_str', endDate);
    analyzeUrl.searchParams.append('client_id', clientId);
    analyzeUrl.searchParams.append('force_analyze', 'false');

    const analyzeResponse = await apiFetch(analyzeUrl.toString(), {
      method: 'POST',
      mode: 'cors', // Добавлено для решения проблем с CORS
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!analyzeResponse.ok) {
      const analyzeResult = await analyzeResponse.json();
      throw new Error(analyzeResult.detail || analyzeResult.message || "Ошибка при анализе звонков");
    }

    // Шаг 2: Анализ рекомендаций
    const recommendationsUrl = new URL(`${API_BASE_URL}/recommendations/analyze-by-date-range`);
    recommendationsUrl.searchParams.append('start_date_str', startDate);
    recommendationsUrl.searchParams.append('end_date_str', endDate);
    recommendationsUrl.searchParams.append('client_id', clientId);

    const recommendationsResponse = await apiFetch(recommendationsUrl.toString(), {
      method: 'POST',
      mode: 'cors', // Добавлено для решения проблем с CORS
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const recommendationsResult = await recommendationsResponse.json();
    if (!recommendationsResponse.ok) {
      throw new Error(recommendationsResult.detail || recommendationsResult.message || "Ошибка при анализе рекомендаций");
    }

    return recommendationsResult;
  } catch (error) {
    console.error('Error analyzing clinic data:', error);
    throw error;
  }
};

/**
 * Обновление URL дашборда Datalens для клиники
 * @param {string} clinicId - ID клиники
 * @param {string} datalensUrl - Новый URL
 * @returns {Promise<object>} - Результат операции
 */
export const updateDatalensUrl = async (clinicId, datalensUrl) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/clinics/${clinicId}/datalens-url`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ datalens_dashboard_url: datalensUrl }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.message || "Ошибка при обновлении URL дашборда");
    }
    return result;
  } catch (error) {
    console.error('Error updating Datalens URL:', error);
    throw error;
  }
};

export const registerClinic = async (clinicData) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/clinics`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(clinicData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.detail || data.message || "Произошла ошибка при регистрации клиники"
      );
    }

    return data;
  } catch (error) {
    console.error("Error registering clinic:", error);
    throw error;
  }
};

/**
 * Форматирование цены для отображения
 * @param {number} price - Цена
 * @returns {string} - Отформатированная цена
 */
export const formatPrice = (price) => {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(price);
};

/**
 * Генерация отчёта
 * @param {string} clientId - ID клиента AmoCRM
 * @param {object} reportData - Данные для генерации отчёта
 * @returns {Promise} - Ответ от API
 */
export const generateReport = async (clientId, reportData) => {
  try {
    const requestData = {
      ...reportData,
      client_id: clientId,
    };

    const response = await apiFetch(`${API_BASE_URL}/call/reports/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при генерации отчёта");
    }

    return data;
  } catch (error) {
    console.error("Error generating report:", error);
    throw error;
  }
};

/**
 * Получение списка доступных отчётов
 * @param {string} clientId - ID клиента AmoCRM
 * @param {object} params - Параметры запроса (clinic_id, start_date, end_date, report_type, limit, skip)
 * @returns {Promise} - Ответ от API
 */
export const getReports = async (clientId, params = {}) => {
  try {
    // Формируем URL с параметрами запроса
    const queryParams = new URLSearchParams();

    if (clientId) queryParams.append("client_id", clientId);
    if (params.clinic_id) queryParams.append("clinic_id", params.clinic_id);
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);
    if (params.report_type)
      queryParams.append("report_type", params.report_type);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.skip) queryParams.append("skip", params.skip);

    const response = await apiFetch(
      `${API_BASE_URL}/call/reports?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении отчётов");
    }

    return data;
  } catch (error) {
    console.error("Error fetching reports:", error);
    throw error;
  }
};

/**
 * Получение детальной информации об отчёте по его ID
 * @param {string} reportId - ID отчёта
 * @returns {Promise} - Ответ от API
 */
export const getReportById = async (reportId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/call/reports/${reportId}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении отчёта");
    }

    return data;
  } catch (error) {
    console.error(`Error fetching report with ID ${reportId}:`, error);
    throw error;
  }
};

/**
 * Формирование URL для скачивания отчёта
 * @param {string} filename - Имя файла отчёта
 * @returns {string} - URL для скачивания
 */
export const getReportDownloadUrl = (filename) => {
  return `${API_BASE_URL}/call/reports/${filename}/download`;
};

/**
 * Проверка формата даты (ДД.ММ.ГГГГ)
 * @param {string} date - Дата для проверки
 * @returns {boolean} - Валидность даты
 */
export const isValidDateFormat = (date) => {
  // Регулярное выражение для проверки формата даты ДД.ММ.ГГГГ
  const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/;
  return dateRegex.test(date);
};

/**
 * Синхронизация звонков по дате с использованием параллельной обработки на сервере
 * @param {string} date - Дата в формате ДД.ММ.ГГГГ
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} concurrency - Количество параллельных задач (по умолчанию 5)
 * @returns {Promise} - Ответ от API
 */
export const syncCallsByDateParallel = async (
  date,
  clientId,
  concurrency = 5
) => {
  try {
    // Формируем URL с параметрами запроса
    const queryParams = new URLSearchParams();
    if (date) queryParams.append("date", date);
    if (clientId) queryParams.append("client_id", clientId);
    queryParams.append("concurrency", concurrency);

    console.log(
      `Начало параллельной синхронизации звонков (concurrency=${concurrency})...`
    );

    const response = await fetchWithRetry(
      `${API_BASE_URL}/calls-parallel-bulk/sync-by-date?${queryParams.toString()}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: "",
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Произошла ошибка при синхронизации звонков"
      );
    }

    return data;
  } catch (error) {
    // Используем обработчик ошибок с возможностью повторного запроса
    return handleApiError(
      error,
      clientId,
      syncCallsByDateParallel,
      date,
      clientId,
      concurrency
    );
  }
};

/**
 * Синхронизация звонков по дате
 * @param {string} date - Дата в формате ДД.ММ.ГГГГ
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} timeout - Время ожидания в миллисекундах (10 минут по умолчанию)
 * @returns {Promise} - Ответ от API
 */
export const syncCallsByDate = async (
  date,
  clientId,
  timeout = DEFAULT_TIMEOUT
) => {
  // Используем новый метод с параллельной обработкой вместо старого
  console.log(
    `Синхронизация звонков с использованием параллельного процессинга...`
  );
  return syncCallsByDateParallel(date, clientId, 5);
};

/**
 * Получение списка звонков из базы за период
 * @param {string} startDate - Начальная дата в формате ГГГГ-ММ-ДД
 * @param {string} endDate - Конечная дата в формате ГГГГ-ММ-ДД
 * @param {number} limit - Максимальное количество звонков
 * @param {number} skip - Количество записей для пропуска (для пагинации)
 * @returns {Promise} - Ответ от API
 */
export const getCallsList = async (
  startDate,
  endDate,
  limit = 50,
  skip = 0
) => {
  try {
    // Формируем URL с параметрами запроса
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append("start_date", startDate);
    if (endDate) queryParams.append("end_date", endDate);
    if (limit) queryParams.append("limit", limit);
    if (skip !== undefined) queryParams.append("skip", skip);

    const response = await apiFetch(
      `${API_BASE_URL}/calls/list?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Произошла ошибка при получении списка звонков"
      );
    }

    return data;
  } catch (error) {
    console.error("Error fetching calls list:", error);
    throw error;
  }
};

/**
 * Загрузка и транскрибация звонка по ID
 * @param {string} callId - ID звонка
 * @param {number} numSpeakers - Количество говорящих для распознавания
 * @returns {Promise} - Ответ от API
 */
export const downloadAndTranscribeCallById = async (
  callId,
  numSpeakers = 2
) => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/calls/download-and-transcribe/${callId}?num_speakers=${numSpeakers}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Произошла ошибка при загрузке и транскрибации звонка"
      );
    }

    return data;
  } catch (error) {
    console.error("Error downloading and transcribing call:", error);
    throw error;
  }
};

/**
 * Скачивание транскрибации по имени файла
 * @param {string} filename - Имя файла транскрибации
 * @returns {string} - URL для скачивания файла
 */
export const downloadTranscriptionByFilename = (filename) => {
  const safe = encodeURIComponent(filename || "");
  return `${API_BASE_URL}/transcriptions/${safe}/download`;
};

/**
 * Анализ звонка по ID
 * @param {string} callId - ID звонка
 * @returns {Promise} - Ответ от API
 */
export const analyzeCallById = async (callId) => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/call/analyze-call-new/${callId}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при анализе звонка");
    }

    return data;
  } catch (error) {
    console.error("Error analyzing call:", error);
    throw error;
  }
};

/**
 * Генерация отчёта по звонкам (новый метод)
 * @param {object} reportData - Данные для генерации отчёта (start_date, end_date, clinic_id)
 * @returns {Promise} - Ответ от API
 */
// Помощник для форматирования даты из YYYY-MM-DD в DD.MM.YYYY
const formatDateForAPI = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return "";
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return dateStr; // Возвращаем как есть, если формат не YYYY-MM-DD
  return `${day}.${month}.${year}`;
};

export const generateCallReportNew = async (reportData) => {
  try {
    // 1. Форматируем и очищаем данные для payload
    const payload = {
      start_date: formatDateForAPI(reportData.start_date),
      end_date: formatDateForAPI(reportData.end_date),
    };

    // 2. Добавляем clinic_id только если он предоставлен (не пустая строка, не null, не undefined)
    if (reportData.clinic_id) {
      payload.clinic_id = reportData.clinic_id;
    }

    console.log('Итоговый payload для отправки:', payload);

    const response = await apiFetch(
      `${API_BASE_URL}/reports/generate_call_report`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    // 3. Детально логируем ответ
    const responseText = await response.text();
    console.log(`Статус ответа: ${response.status} ${response.statusText}`);
    console.log('Сырой ответ от сервера:', responseText);

    if (!response.ok) {
      let errorData;
      try {
        // Пытаемся распарсить ошибку как JSON
        errorData = JSON.parse(responseText);
      } catch (e) {
        // Если тело ответа не JSON, используем сам текст как сообщение об ошибке
        throw new Error(responseText || "Произошла неизвестная ошибка при генерации отчёта");
      }
      // Используем поле detail (от FastAPI) или message
      throw new Error(errorData.detail || errorData.message || "Произошла ошибка при генерации отчёта");
    }

    // Если ответ успешный, парсим его как JSON
    const data = JSON.parse(responseText);
    console.log("Успешный ответ от API:", data);
    return data;

  } catch (error) {
    // Логируем финальную ошибку, которая будет показана пользователю
    console.error("Error in generateCallReportNew:", error.message);
    throw error;
  }
};

/**
 * Скачивание отчёта по имени файла
 * @param {string} filename - Имя файла отчёта
 * @returns {string} - URL для скачивания файла
 */
export const downloadCallReportByFilename = (filename) => {
  return `${API_BASE_URL}/call/reports/${filename}/download`;
};

/**
 * Скачивание отчёта в формате Excel
 * @param {string} startDate - Начальная дата в формате ДД.ММ.ГГГГ
 * @param {string} endDate - Конечная дата в формате ДД.ММ.ГГГГ
 * @param {string} clinicId - ID клиники
 * @returns {Promise} - Промис с именем файла для скачивания
 */
const formatDateForApi = (dateString) => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString; // Возвращаем как есть, если формат не YYYY-MM-DD или пустой
  }
  const [year, month, day] = dateString.split('-');
  return `${day}.${month}.${year}`;
};

export const downloadExcelReport = async (startDate, endDate, clinicId) => {
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.append("start_date", formatDateForApi(startDate));
  if (endDate) queryParams.append("end_date", formatDateForApi(endDate));
  if (clinicId) queryParams.append("clinic_id", clinicId);

  try {
    const response = await apiFetch(`${API_BASE_URL}/reports/generate-excel?${queryParams.toString()}`, {
      method: "GET",
      headers: {
        "Accept": "application/json"
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при генерации Excel-отчёта");
    }

    if (data.status === "success" && data.filename) {
      return data.filename;
    } else {
      throw new Error("Не удалось получить имя файла Excel-отчёта");
    }
  } catch (error) {
    console.error("Ошибка при скачивании Excel-отчёта:", error);
    throw error;
  }
};

/**
 * Скачивание Excel-отчёта по имени файла
 * @param {string} filename - Имя файла Excel-отчёта
 * @returns {string} - URL для скачивания файла
 */
export const downloadExcelReportByFilename = (filename) => {
  // Восстановлен правильный URL для скачивания отчета
  return `${API_BASE_URL}/call/reports/${filename}/download`;
};

/**
 * Скачивание аудиофайла звонка
 * @param {string} callId - ID звонка
 * @returns {string} - URL для скачивания аудиофайла
 */
export const downloadCallAudio = (callId) => {
  return `${API_BASE_URL}/calls/download/${callId}`;
};

/**
 * Получение сделок через API звонков
 * @param {string} startDate - Начальная дата в формате ДД.ММ.ГГГГ
 * @param {string} endDate - Конечная дата в формате ДД.ММ.ГГГГ
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} limit - Максимальное количество записей
 * @param {number} skip - Количество записей для пропуска (для пагинации)
 * @param {number} timeout - Время ожидания в миллисекундах (10 минут по умолчанию)
 * @returns {Promise} - Ответ от API со списком сделок и звонков
 */
export const getLeadsFromCalls = async (
  startDate,
  endDate,
  clientId,
  limit = 50,
  skip = 0,
  timeout = DEFAULT_TIMEOUT
) => {
  try {
    // Формируем URL с параметрами запроса
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append("start_date", startDate);
    if (endDate) queryParams.append("end_date", endDate);
    if (clientId) queryParams.append("client_id", clientId);
    if (limit) queryParams.append("limit", limit);
    if (skip !== undefined) queryParams.append("skip", skip);

    console.log(
      `Получение звонков из базы (таймаут: ${timeout / 1000} сек)...`
    );

    const response = await fetchWithRetry(
      `${API_BASE_URL}/calls/list?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении сделок");
    }

    return data;
  } catch (error) {
    // Используем обработчик ошибок с возможностью повторного запроса
    return handleApiError(
      error,
      clientId,
      getLeadsFromCalls,
      startDate,
      endDate,
      clientId,
      limit,
      skip,
      timeout
    );
  }
};

/**
 * Получение всех звонков из базы данных с пагинацией
 * @param {string} clientId - ID клиента (может быть null, если не требуется фильтрация по клинике на бэкенде)
 * @param {number} limit - Количество записей для получения
 * @param {number} skip - Количество записей для пропуска (для пагинации)
 * @param {string} startDate - Начальная дата в формате ДД.ММ.ГГГГ
 * @param {string} endDate - Конечная дата в формате ДД.ММ.ГГГГ
 * @param {number} timeout - Таймаут для запроса
 * @returns {Promise} - Ответ от API
 */
export const getAllCallsFromDb = async (
  clientId = null,
  limit = 100,
  skip = 0,
  startDate = null,
  endDate = null,
  filters = {},
  timeout = DEFAULT_TIMEOUT
) => {
  try {
    const queryParams = new URLSearchParams();
    if (clientId) {
      queryParams.append("client_id", clientId);
    }
    queryParams.append("limit", limit.toString());
    queryParams.append("skip", skip.toString());

    if (startDate) {
      queryParams.append("start_date", startDate);
    }
    if (endDate) {
      queryParams.append("end_date", endDate);
    }

    // Поддержка фильтрации по эффективности
    if (filters && filters.is_effective !== undefined && filters.is_effective !== null) {
      queryParams.append("is_effective", filters.is_effective.toString());
    }

    console.log(
      `Получение всех звонков из базы (лимит: ${limit}, пропуск: ${skip}, таймаут: ${timeout / 1000} сек)...`
    );

    const response = await fetchWithRetry(
      `${API_BASE_URL}/calls/list?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
      MAX_RETRY_ATTEMPTS, // Используем стандартное количество попыток
      RETRY_DELAY,        // Используем стандартную задержку
      timeout             // Передаем таймаут в fetchWithRetry, если он его поддерживает, или используем fetchWithTimeout
      // Если fetchWithRetry не поддерживает таймаут напрямую, его нужно обернуть или модифицировать.
      // Судя по коду fetchWithRetry, он не управляет таймаутом запроса, только повторами.
      // Правильнее было бы использовать fetchWithTimeout здесь.
      // Однако, для простоты и консистентности с getLeadsFromCalls, пока оставим так,
      // предполагая, что таймаут контролируется на уровне fetch или серверной стороны.
      // В идеале, fetchWithRetry должен быть интегрирован с fetchWithTimeout.
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении списка звонков");
    }

    return data; // Ожидается, что data будет иметь структуру { success: true, data: { calls: [], total: N } } или аналогичную
  } catch (error) {
    // Используем обработчик ошибок с возможностью повторного запроса
    // Передаем clientId для возможности обновления токена
    return handleApiError(
      error,
      clientId,
      getAllCallsFromDb, // Функция для повторного вызова
      clientId,          // Параметры для повторного вызова
      limit,
      skip,
      timeout
    );
  }
};

/**
 * Получение детальной информации о сделке по её ID
 * @param {string} clientId - ID клиента AmoCRM
 * @param {number} leadId - ID сделки
 * @returns {Promise} - Ответ от API
 */
export const getLeadDetails = async (clientId, leadId) => {
  try {
    // Сначала попробуем получить информацию из звонков
    const callsData = await getLeadsFromCalls(null, null, clientId, 100, 0);

    // Ищем сделку в звонках
    if (callsData && callsData.calls) {
      const leadCall = callsData.calls.find(
        (call) => call.lead_id === Number(leadId)
      );
      if (leadCall) {
        return {
          success: true,
          data: {
            id: leadCall.lead_id,
            name: leadCall.lead_name,
            contact: {
              id: leadCall.contact_id,
              name: leadCall.contact_name,
              phone: leadCall.phone?.split(",")[0]?.trim() || "",
            },
            administrator: leadCall.administrator,
            source: leadCall.source,
            created_at: leadCall.created_date,
            call: {
              duration: leadCall.duration_formatted,
              call_link: leadCall.call_link,
              note_id: leadCall.note_id,
              direction: leadCall.call_direction,
              call_id: leadCall._id,
            },
          },
        };
      }
    }

    // Если не найдено в звонках, используем старый метод
    const response = await apiFetch(`${API_BASE_URL}/amocrm/leads/get`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        client_id: clientId,
        lead_id: leadId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Произошла ошибка при получении сделки");
    }

    return data;
  } catch (error) {
    console.error(`Error fetching lead details for lead ID ${leadId}:`, error);
    throw error;
  }
};

/**
 * Скачивание и транскрибация звонка из результатов getLeadDetails
 * @param {object} leadDetails - Данные сделки, полученные из getLeadDetails
 * @param {number} numSpeakers - Количество говорящих для распознавания
 * @returns {Promise} - Ответ от API
 */
export const transcribeLeadCall = async (leadDetails, numSpeakers = 2) => {
  try {
    if (
      !leadDetails ||
      !leadDetails.data ||
      !leadDetails.data.call ||
      !leadDetails.data.call.call_id
    ) {
      throw new Error("Не удалось найти ID звонка в данных сделки");
    }

    const callId = leadDetails.data.call.call_id;
    console.log(`Скачивание и транскрибация звонка с ID: ${callId}`);

    // Используем новый метод API
    return await downloadAndTranscribeCallById(callId, numSpeakers);
  } catch (error) {
    console.error("Error transcribing lead call:", error);
    throw error;
  }
};

// ===== НОВЫЕ API ФУНКЦИИ ДЛЯ СИСТЕМЫ ПРОЦЕССОВ =====

// 1. Запуск синхронизации звонков
export const startSyncProcess = async (clientId, startDate, endDate) => {
  try {
    // Используем URL параметры как в существующей функции syncAndTranscribeClinicData
    const syncUrl = new URL(`${API_BASE_URL}/calls-parallel-bulk/sync-by-date-range`);
    syncUrl.searchParams.append('start_date_str', startDate);
    syncUrl.searchParams.append('end_date_str', endDate);
    syncUrl.searchParams.append('client_id', clientId);

    const response = await apiFetch(syncUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка при запуске синхронизации');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка синхронизации:', error);
    throw error;
  }
};

// 2. Запуск транскрибации
export const startTranscriptionProcess = async (clientId, startDate, endDate) => {
  try {
    // Используем URL параметры как в существующей функции syncAndTranscribeClinicData
    const transcribeUrl = new URL(`${API_BASE_URL}/calls/transcribe-by-date-range`);
    transcribeUrl.searchParams.append('start_date_str', startDate);
    transcribeUrl.searchParams.append('end_date_str', endDate);
    transcribeUrl.searchParams.append('client_id', clientId);

    const response = await apiFetch(transcribeUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка при запуске транскрибации');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка транскрибации:', error);
    throw error;
  }
};

// 3. Запуск анализа звонков
export const startAnalysisProcess = async (clientId, startDate, endDate) => {
  try {
    // Используем URL параметры как в синхронизации и транскрибации
    const analyzeUrl = new URL(`${API_BASE_URL}/analyze-by-date-range`);
    analyzeUrl.searchParams.append('start_date_str', startDate);
    analyzeUrl.searchParams.append('end_date_str', endDate);
    analyzeUrl.searchParams.append('client_id', clientId);

    const response = await apiFetch(analyzeUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка при запуске анализа');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка анализа:', error);
    throw error;
  }
};

// 4. Запуск анализа рекомендаций
export const startRecommendationsProcess = async (clientId, startDate, endDate) => {
  try {
    // Используем URL параметры как в синхронизации и транскрибации
    const recommendationsUrl = new URL(`${API_BASE_URL}/recommendations/analyze-by-date-range`);
    recommendationsUrl.searchParams.append('start_date_str', startDate);
    recommendationsUrl.searchParams.append('end_date_str', endDate);
    recommendationsUrl.searchParams.append('client_id', clientId);

    const response = await apiFetch(recommendationsUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка при запуске анализа рекомендаций');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка анализа рекомендаций:', error);
    throw error;
  }
};

// ===== ФУНКЦИИ ПРОВЕРКИ СТАТУСОВ =====

// Проверка статуса транскрибации
export const checkTranscriptionStatus = async (clientId, startDate, endDate) => {
  try {
    const params = new URLSearchParams({
      start_date_str: startDate,
      end_date_str: endDate,
      client_id: clientId
    });

    const response = await apiFetch(`${API_BASE_URL}/calls/transcribe-by-date-range-status?${params}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка проверки статуса транскрибации');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка проверки статуса транскрибации:', error);
    throw error;
  }
};

// Проверка статуса анализа звонков
export const checkAnalysisStatus = async (clientId, startDate, endDate) => {
  try {
    // Для анализа используем тот же формат что и для запуска процесса
    const params = new URLSearchParams({
      start_date_str: startDate,
      end_date_str: endDate,
      client_id: clientId
    });

    const response = await apiFetch(`${API_BASE_URL}/analyze-by-date-range-status?${params}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка проверки статуса анализа');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка проверки статуса анализа:', error);
    throw error;
  }
};

// Проверка статуса анализа рекомендаций
export const checkRecommendationsStatus = async (clientId, startDate, endDate) => {
  try {
    // Для анализа рекомендаций используем тот же формат что и для запуска процесса
    const params = new URLSearchParams({
      start_date_str: startDate,
      end_date_str: endDate,
      client_id: clientId
    });

    const response = await apiFetch(`${API_BASE_URL}/recommendations/analyze-by-date-range-status?${params}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Ошибка проверки статуса анализа рекомендаций');
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка проверки статуса анализа рекомендаций:', error);
    throw error;
  }
};

// ========================================
// API для управления графиками работы администраторов
// ========================================

/**
 * Создание записей графика работы
 * @param {object} data - Данные для создания графика
 * @param {string} data.clinic_id - UUID клиники
 * @param {string[]} data.dates - Массив дат в формате YYYY-MM-DD
 * @param {string} data.first_name - Имя администратора
 * @param {string} data.last_name - Фамилия администратора
 * @returns {Promise<object>} - Ответ от API
 */
export const createWorkSchedules = async (data) => {
  try {
    console.log("Отправка данных графика:", JSON.stringify(data, null, 2));
    const response = await apiFetch(`${API_BASE_URL}/work-schedules`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error("Ошибка от сервера:", error);
      throw new Error(error.detail || error.message || "Ошибка при создании графика работы");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при создании графика работы:", error);
    throw error;
  }
};

/**
 * Получение графиков работы
 * @param {string} clinicId - UUID клиники
 * @param {string} [dateFrom] - Начальная дата в формате YYYY-MM-DD
 * @param {string} [dateTo] - Конечная дата в формате YYYY-MM-DD
 * @returns {Promise<object>} - Ответ от API
 */
export const getWorkSchedules = async (clinicId, dateFrom = null, dateTo = null) => {
  try {
    const params = new URLSearchParams({ clinic_id: clinicId });
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);

    const response = await apiFetch(`${API_BASE_URL}/work-schedules?${params}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при получении графиков работы");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении графиков работы:", error);
    throw error;
  }
};

/**
 * Редактирование записи графика работы
 * @param {string} scheduleId - ID записи графика
 * @param {object} data - Данные для обновления
 * @param {string} [data.first_name] - Новое имя
 * @param {string} [data.last_name] - Новая фамилия
 * @param {string} [data.date] - Новая дата в формате YYYY-MM-DD
 * @returns {Promise<object>} - Ответ от API
 */
export const updateWorkSchedule = async (scheduleId, data) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/work-schedules/${scheduleId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при обновлении графика работы");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении графика работы:", error);
    throw error;
  }
};

/**
 * Удаление записи графика работы
 * @param {string} scheduleId - ID записи графика
 * @returns {Promise<object>} - Ответ от API
 */
export const deleteWorkSchedule = async (scheduleId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/work-schedules/${scheduleId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при удалении графика работы");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при удалении графика работы:", error);
    throw error;
  }
};

/**
 * Получение списка администраторов клиники
 * @param {string} clinicId - UUID клиники
 * @returns {Promise<Array>} - Массив администраторов
 */
export const getAdministrators = async (clinicId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/administrators?clinic_id=${clinicId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при получении администраторов");
    }

    const data = await response.json();
    return data.administrators || [];
  } catch (error) {
    console.error("Ошибка при получении администраторов:", error);
    throw error;
  }
};

/**
 * Получение настроек клиники
 * @param {string} clinicId - UUID клиники
 * @returns {Promise<object>} - Настройки клиники
 */
export const getClinicSettings = async (clinicId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/clinics/${clinicId}/settings`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при получении настроек клиники");
    }

    const result = await response.json();
    return result.data || result; // Поддержка обоих форматов ответа
  } catch (error) {
    console.error("Ошибка при получении настроек клиники:", error);
    throw error;
  }
};

/**
 * Обновление настроек клиники
 * @param {string} clinicId - UUID клиники
 * @param {object} settings - Настройки для обновления
 * @param {string} [settings.admin_detection_method] - Метод определения администратора: "amocrm" или "ai_schedule"
 * @returns {Promise<object>} - Ответ от API
 */
export const updateClinicSettings = async (clinicId, settings) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/clinics/${clinicId}/settings`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при обновлении настроек клиники");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении настроек клиники:", error);
    throw error;
  }
};

// ========================================
// API для управления лимитами клиник
// ========================================

/**
 * Получение баланса одной клиники
 * @param {string} clientId - UUID клиники
 * @returns {Promise<object>} - Ответ от API
 */
export const getClinicLimits = async (clientId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/clinics/${clientId}/limits`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при получении лимитов клиники");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении лимитов клиники:", error);
    throw error;
  }
};

/**
 * Получение баланса всех клиник
 * @returns {Promise<object>} - Ответ от API
 */
export const getAllClinicsLimits = async () => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/clinics/limits/all`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при получении лимитов клиник");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении лимитов клиник:", error);
    throw error;
  }
};

/**
 * Обновление лимитов клиники
 * @param {string} clientId - UUID клиники
 * @param {object} data - Данные для обновления
 * @param {number} data.monthly_limit_minutes - Новый месячный лимит
 * @param {number} [data.current_usage_minutes] - Сбросить использование (опционально)
 * @returns {Promise<object>} - Ответ от API
 */
export const updateClinicLimits = async (clientId, data) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/clinics/${clientId}/limits`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при обновлении лимитов клиники");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении лимитов клиники:", error);
    throw error;
  }
};

/**
 * Сброс использованных минут
 * @param {string} clientId - UUID клиники
 * @param {string} resetType - Тип сброса: "monthly", "weekly" или "both"
 * @returns {Promise<object>} - Ответ от API
 */
export const resetClinicLimits = async (clientId, resetType = "monthly") => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/admin/clinics/${clientId}/limits/reset?reset_type=${resetType}`,
      {
        method: "POST",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при сбросе лимитов клиники");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при сбросе лимитов клиники:", error);
    throw error;
  }
};

// ========================================
// API для управления критериями эффективности
// ========================================

/**
 * Получение критериев эффективности для клиники
 * @param {string} clinicId - UUID клиники
 * @param {string} callType - Тип звонка (например, "initial", "secondary")
 * @returns {Promise<object>} - Настройки критериев
 */
export const getEfficiencyCriteria = async (clinicId, callType) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/efficiency-criteria/${clinicId}/${callType}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при получении критериев эффективности");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении критериев эффективности:", error);
    throw error;
  }
};

/**
 * Обновление/создание критериев эффективности
 * @param {object} data - Данные критериев (EfficiencyCriteria модель)
 * @returns {Promise<object>} - Ответ от API
 */
export const updateEfficiencyCriteria = async (data) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/efficiency-criteria`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || "Ошибка при обновлении критериев эффективности");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении критериев эффективности:", error);
    throw error;
  }
};

/**
 * Получение настроек критериев эффективности клиники
 * @param {string} clinicId - ID клиники
 * @returns {Promise} - Ответ от API
 */
export const getClinicEfficiencySettings = async (clinicId) => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/efficiency/${clinicId}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    // Обработка 404 - возвращаем пустой массив, если настройки еще не созданы
    if (response.status === 404) {
      return [];
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || data.message || "Не удалось получить настройки эффективности");
    }

    return data;
  } catch (error) {
    console.error('Error fetching efficiency settings:', error);
    throw error;
  }
};

/**
 * Обновление настроек критериев эффективности клиники
 * @param {string} clinicId - ID клиники
 * @param {object} settings - Объект с настройками
 * @returns {Promise} - Ответ от API
 */
export const updateClinicEfficiencySettings = async (clinicId, settings) => {
  try {
    const callType = settings.call_type;
    if (!callType) throw new Error("Тип звонка не указан");

    const response = await apiFetch(`${API_BASE_URL}/efficiency/${clinicId}/${callType}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || data.message || 'Не удалось сохранить настройки эффективности');
    }

    return data;
  } catch (error) {
    console.error('Error updating efficiency settings:', error);
    throw error;
  }
};
